import isEqual from "lodash/isEqual";
import { useCallback, useRef, useMemo } from "react";

import { getConversationIdentifier as getConversationModelIdentifier } from "./__internal__/ConversationModel";
import {
  removeReadConversations,
  removeNonStarredByBrandConversations,
} from "./__internal__/MainStreamFetchState";
import { useMessengerContext } from "./__internal__/MessengerContext";
import { useMessengerStoreConversationFetching } from "./__internal__/useMessengerStoreConversationFetching";
import { useMessengerStoreOrderedConversations } from "./__internal__/useMessengerStoreOrderedConversations";
import { useMessengerStoreGetConversation } from "./useMessengerStoreGetConversation";
import { useMessengerStoreHasConversations } from "./useMessengerStoreHasConversations";
import { useMessengerStoreIsLoadingConversations } from "./useMessengerStoreIsLoadingConversations";

export const useMessengerStoreConversationList = (): {
  latestUnreadConversationOtherPartyFirstName: string | undefined;
  orderedConversationIdentifiers: string[];
  hasConversations: boolean;
  isLoadingConversations: boolean;
  unreadConversationCount: number;
  setTotalUnreadConversations: (count: number | undefined) => void;
  starredByBrandConversationCount: number;
  shouldFilterByUnreadConversations: boolean;
  shouldFilterByBrandStarredConversations: boolean;
  setShouldFilterByUnreadConversations: (value: boolean) => void;
  setShouldFilterByBrandStarredConversations: (value: boolean) => void;
  nextConversations: () => Promise<void>;
  hasConversationsNextRequest: boolean;
} => {
  const { hasConversations } = useMessengerStoreHasConversations();
  const { isLoadingConversations } = useMessengerStoreIsLoadingConversations();
  const { orderedConversations } = useMessengerStoreOrderedConversations();

  const totalUnreadConversations = useMessengerContext(
    ({ totalUnreadConversations }) => totalUnreadConversations
  );
  const totalStarredByBrandConversations = useMessengerContext(
    ({ totalStarredByBrandConversations }) => totalStarredByBrandConversations
  );
  const setTotalUnreadConversationsInContext = useMessengerContext(
    ({ setTotalUnreadConversations }) => setTotalUnreadConversations
  );
  const showOnlyUnreadConversations = useMessengerContext(
    ({ showOnlyUnreadConversations }) => showOnlyUnreadConversations
  );
  const showOnlyStarredByBrandConversations = useMessengerContext(
    ({ showOnlyStarredByBrandConversations }) =>
      showOnlyStarredByBrandConversations
  );
  const setShowOnlyUnreadConversationsInContext = useMessengerContext(
    ({ setShowOnlyUnreadConversations }) => setShowOnlyUnreadConversations
  );
  const setShowOnlyStarredByBrandConversationsInContext = useMessengerContext(
    ({ setShowOnlyStarredByBrandConversations }) =>
      setShowOnlyStarredByBrandConversations
  );
  const setUnreadOnlyMainStreamFetchState = useMessengerContext(
    ({ setUnreadOnlyMainStreamFetchState }) => setUnreadOnlyMainStreamFetchState
  );
  const setStarredByBrandOnlyMainStreamFetchState = useMessengerContext(
    ({ setStarredByBrandOnlyMainStreamFetchState }) =>
      setStarredByBrandOnlyMainStreamFetchState
  );

  const { nextConversations, hasConversationsNextRequest } =
    useMessengerStoreConversationFetching();

  const { getConversationIsUnread, getConversationOtherPartyFirstName } =
    useMessengerStoreGetConversation();

  const unreadConversationCount = totalUnreadConversations ?? 0;
  const starredByBrandConversationCount = totalStarredByBrandConversations ?? 0;

  const memoizedOrderedConversationIdentifiers = useRef<string[]>([]);

  // This cannot be memoized (yet) because there's no way to specify the dependencies
  // on `orderedConversations`, as it's still updated via mutation. However this itself
  // will be re-created on update so can be used as a reactive dependency to other
  // useMemo etc.
  const orderedConversationIdentifiers = useMemo(() => {
    const newOrderedConversationIdentifiers = orderedConversations.map(
      (conversation) => getConversationModelIdentifier(conversation) ?? ""
    );

    if (
      !isEqual(
        newOrderedConversationIdentifiers,
        memoizedOrderedConversationIdentifiers.current
      )
    ) {
      memoizedOrderedConversationIdentifiers.current =
        newOrderedConversationIdentifiers;
      return newOrderedConversationIdentifiers;
    } else {
      return memoizedOrderedConversationIdentifiers.current;
    }
  }, [orderedConversations]);

  const latestUnreadConversationToken: string | undefined =
    orderedConversationIdentifiers.find((conversationIdentifier: string) =>
      getConversationIsUnread(conversationIdentifier)
    );

  const latestUnreadConversationOtherPartyFirstName: string | undefined =
    latestUnreadConversationToken
      ? getConversationOtherPartyFirstName(latestUnreadConversationToken)
      : undefined;

  const setShouldFilterByUnreadConversations = useCallback(
    async (shouldFilterByUnreadConversations: boolean) => {
      if (
        !setShowOnlyUnreadConversationsInContext ||
        !setUnreadOnlyMainStreamFetchState
      ) {
        return;
      }

      setShowOnlyUnreadConversationsInContext(
        shouldFilterByUnreadConversations
      );
      if (shouldFilterByUnreadConversations) {
        setUnreadOnlyMainStreamFetchState(removeReadConversations);
      }
    },
    [setUnreadOnlyMainStreamFetchState, setShowOnlyUnreadConversationsInContext]
  );

  const setShouldFilterByBrandStarredConversations = useCallback(
    async (shouldFilterByBrandStarredConversations: boolean) => {
      if (
        !setShowOnlyStarredByBrandConversationsInContext ||
        !setStarredByBrandOnlyMainStreamFetchState
      ) {
        return;
      }

      setShowOnlyStarredByBrandConversationsInContext(
        shouldFilterByBrandStarredConversations
      );
      if (shouldFilterByBrandStarredConversations) {
        setStarredByBrandOnlyMainStreamFetchState(
          removeNonStarredByBrandConversations
        );
      }
    },
    [
      setStarredByBrandOnlyMainStreamFetchState,
      setShowOnlyStarredByBrandConversationsInContext,
    ]
  );

  const setTotalUnreadConversations = useCallback(
    (count: number | undefined) => {
      setTotalUnreadConversationsInContext?.(count);
    },
    [setTotalUnreadConversationsInContext]
  );

  // Note: `...` spread syntax misses some fields here, not sure why, but we
  // list each field explicitly to prevent this.
  return {
    latestUnreadConversationOtherPartyFirstName,
    orderedConversationIdentifiers,
    hasConversations,
    isLoadingConversations,
    unreadConversationCount,
    setTotalUnreadConversations,
    starredByBrandConversationCount,
    shouldFilterByUnreadConversations: showOnlyUnreadConversations ?? false,
    shouldFilterByBrandStarredConversations:
      showOnlyStarredByBrandConversations ?? false,
    setShouldFilterByUnreadConversations,
    setShouldFilterByBrandStarredConversations,
    nextConversations,
    hasConversationsNextRequest,
  };
};
